import { SiteImage } from "@inrange/api-clients";
import {
  Building,
  getBuildingCoordinates,
} from "@inrange/building-manager-api-client/models-site";
import { Map, StyledGeoJsonGroup } from "@inrange/theme-components/mapping";
import * as Sentry from "@sentry/react";
import { Feature, LineString, Polygon } from "geojson";
import L, { latLngBounds, LatLngBounds } from "leaflet";
import "leaflet-editable";
import "leaflet.path.drag";
import "leaflet/dist/leaflet.css";
import React, { useEffect, useMemo, useState } from "react";
import Form from "react-bootstrap/Form";
import useIsMobile from "../../../../utils/useIsMobile";
import { UseQueryResult } from "@tanstack/react-query";
import {
  getStyle,
  outlineGreenStyle,
} from "../PVCustomObjects/pvcustomobjectmap-utils";

interface PvMapProps {
  siteId: string;
  siteCenter: [number, number];
  buildings: Building[];
  panelsUrl: string | undefined;
  customObjects: (Feature<Polygon> | Feature<LineString>)[];
  siteImage?: SiteImage;
  usePresignedUrlData: <T>(
    siteId: string,
    urlType: string,
    url: string | undefined
  ) => UseQueryResult<T>;
  showPanels?: boolean;
  /**
   * Caller-side hook for the Leaflet map instance. Fires when the
   * map mounts (with the map) and unmounts (with undefined). Lets
   * callers wire keyboard shortcuts that drive zoom / pan from
   * outside the map component itself.
   */
  onMapReady?: (map: L.Map | undefined) => void;
  /**
   * Hide PvMap's own "Satellite imagery cached on..." line - useful
   * when the caller wants to render its own (e.g. the bulk-review
   * surface shows it in the per-site title bar instead).
   */
  hideSatelliteCachedNotice?: boolean;
}

export const PvMapRender: React.FC<PvMapProps> = ({
  siteId,
  siteCenter,
  buildings,
  panelsUrl,
  customObjects,
  siteImage,
  usePresignedUrlData,
  showPanels = true,
  onMapReady,
  hideSatelliteCachedNotice,
}) => {
  const isMobile = useIsMobile();
  const panelObjectsQuery = usePresignedUrlData<string[][]>(
    siteId,
    "panel_objects",
    panelsUrl
  );
  const [map, setMap] = useState<L.Map | undefined>();

  // Notify the caller when the leaflet map instance is available
  // (and again with undefined when this component unmounts).
  useEffect(() => {
    if (!onMapReady) return;
    onMapReady(map);
    return () => onMapReady(undefined);
  }, [map, onMapReady]);

  useEffect(() => {
    const bounds: LatLngBounds = latLngBounds([
      L.latLng(siteCenter[0], siteCenter[1]),
    ]);
    for (const building of buildings) {
      const buildingCoordinates = getBuildingCoordinates(building);
      for (const buildingCoordinate of buildingCoordinates[0]) {
        bounds.extend(L.latLng(buildingCoordinate[1], buildingCoordinate[0]));
      }
    }
    if (map) {
      try {
        map.flyToBounds(bounds);
      } catch (e) {
        // We know this can happen, possibly related to https://github.com/Leaflet/Leaflet/issues/9527
        Sentry.captureException(e);
      }
    }
  }, [map, siteCenter, buildings]);

  const styledGeoJsonGroups: StyledGeoJsonGroup[] = useMemo(() => {
    const buildingsGeoJson = buildings.map((building) => {
      const geoJsonBuilding: Polygon = {
        ...building.geometry,
        coordinates: getBuildingCoordinates(building),
        type: "Polygon",
      };
      return {
        key: building.id,
        geoJson: geoJsonBuilding,
        color: outlineGreenStyle.color,
      };
    });

    if (!panelObjectsQuery.isSuccess) {
      return [
        { geoJsons: buildingsGeoJson, name: "Buildings", key: "buildings" },
      ];
    }

    const panelsGeoJson = (panelObjectsQuery?.data || [[]])
      .map((buildingPanels) => {
        return buildingPanels.map((panel: string, index: number) => {
          return {
            key: `panel-${index}-${panel}`,
            geoJson: JSON.parse(panel) as Polygon,
            color: "#4578de",
            fillColor: "#ADD8E6",
            fillOpacity: 1,
            weight: 1,
          };
        });
      })
      .flat();

    const objectsGeoJson = customObjects.map((object, index) => {
      const objectColor = getStyle(object.properties!.type, false).color;
      return {
        key: `object-${index}-${object}`,
        geoJson: object,
        color: objectColor,
        fillColor: objectColor,
        fillOpacity: 0.4,
        weight: 1,
      };
    });

    return [
      { geoJsons: buildingsGeoJson, name: "Buildings", key: "buildings" },
      // Only offer the "Roof objects" overlay when there are objects to show.
      // Auto-detected roof objects live in pvaData.geojson, not custom_objects,
      // so in flows without custom objects (e.g. bulk-review) this layer would
      // otherwise be an empty, non-functional toggle.
      ...(objectsGeoJson.length > 0
        ? [{ geoJsons: objectsGeoJson, name: "Roof objects", key: "roofobjects" }]
        : []),
      ...(showPanels
        ? [{ geoJsons: panelsGeoJson, name: "Panels", key: "panels" }]
        : []),
    ];
  }, [
    buildings,
    customObjects,
    panelObjectsQuery?.data,
    panelObjectsQuery.isSuccess,
    showPanels,
  ]);

  if (panelsUrl === undefined || !panelObjectsQuery.isSuccess) {
    return (
      <>
        <div>
          <i>Not yet available.</i>;
        </div>
      </>
    );
  }

  return (
    <>
      <div style={isMobile ? { padding: "0 16px" } : undefined}>
        {!hideSatelliteCachedNotice && siteImage && siteImage.cached_at && (
          <div style={{ marginBottom: "8px" }}>
            <i>
              Satellite imagery cached on{" "}
              {siteImage.cached_at?.replace(/T.*/, "")}
            </i>
          </div>
        )}
        <Map
          width={"100%"}
          height={"500px"}
          lat={siteCenter[0]}
          lon={siteCenter[1]}
          zoom={20}
          styledGeoJsonGroups={styledGeoJsonGroups}
          setMap={setMap}
          useGoogleMapsBaseLayer={true}
          tooltipText={undefined}
          siteImage={siteImage}
          siteImageKey={"image_png"}
        />
      </div>
    </>
  );
};

const PvMap: React.FC<PvMapProps> = ({
  siteId,
  siteCenter,
  buildings,
  panelsUrl,
  customObjects,
  siteImage,
  usePresignedUrlData,
  showPanels,
  onMapReady,
  hideSatelliteCachedNotice,
}) => {
  const [hideMap, setHideMap] = useState<boolean>(true);
  return (
    <>
      <Form.Label>
        <strong>Site Map:</strong> (
        <span
          style={{
            textDecoration: "underline",
            cursor: "pointer",
            color: "rgb(13, 110, 253)",
          }}
          onClick={() => setHideMap(!hideMap)}
        >
          {hideMap ? "show" : "hide"}
        </span>
        )
      </Form.Label>
      {!hideMap && (
        <PvMapRender
          siteId={siteId}
          siteCenter={siteCenter}
          buildings={buildings}
          panelsUrl={panelsUrl}
          customObjects={customObjects}
          siteImage={siteImage}
          usePresignedUrlData={usePresignedUrlData}
          showPanels={showPanels}
          onMapReady={onMapReady}
          hideSatelliteCachedNotice={hideSatelliteCachedNotice}
        />
      )}
    </>
  );
};

export default PvMap;
